import React, { useEffect } from 'react';

import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { type FieldProps, Field, useFormikContext } from 'formik';
import { getTranslate, TranslateFunction } from 'react-localize-redux';
import { connect } from 'react-redux';

import LinkButton from '@fd/ui/Button/LinkButton';
import FormItemLayout from '@fd/ui/Forms/FormItemLayout';
import Select, { OptionType } from '@fd/ui/Select/Select';

import { formikValidate } from '../../../../helpers/validation';
import { SenderId, SenderIdStatus } from '../../settings.types';
import { FormValues } from '../GeneralSettingsForm';

const validateSenderIdField = (value: SenderId) => {
  return formikValidate.senderIdField(value);
};

const getStatusTranslation = (status: SenderIdStatus, translate: TranslateFunction): string => {
  switch (status) {
    case SenderIdStatus.PENDING_COMREG:
      return translate('Sender_ID_Status_pending_comreg') as string;
    case SenderIdStatus.PENDING_TWILIO:
      return translate('Sender_ID_Status_pending_twilio') as string;
    case SenderIdStatus.REJECTED_COMREG:
      return translate('Sender_ID_Status_rejected_comreg') as string;
    case SenderIdStatus.READY:
      return translate('Sender_ID_Status_ready') as string;
    default:
      return status;
  }
};

const StyledContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  paddingRight: theme.spacing(2),

  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
}));

const StyledSelectWrapper = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    flex: 1, // Takes remaining space
  },
}));

const StyledButtonWrapper = styled(Box)(({ theme }) => ({
  alignSelf: 'flex-end',
  justifySelf: 'flex-start',

  [theme.breakpoints.up('md')]: {
    flexShrink: 0, // Button takes only the space it needs
  },
}));

type InnerProps = MappedProps;
type OuterProps = {
  brandId: string;
  isSenderIdsLoading: boolean;
  senderIds: SenderId[];
};
type Props = InnerProps & OuterProps;

const SenderIdField = ({ brandId, isSenderIdsLoading, senderIds, translate }: Props) => {
  const formik = useFormikContext<FormValues>();

  // Auto-select the active sender ID when the field is empty and an active sender exists.
  useEffect(() => {
    if (isSenderIdsLoading) {
      return;
    }

    const currentValue = formik.values.senderId;
    if (currentValue) {
      return; // respect existing selection/value
    }

    const activeSender = senderIds.find((s) => s.isActive);
    if (activeSender) {
      formik.setFieldValue('senderId', activeSender, false);
    }
  }, [isSenderIdsLoading, senderIds, formik.values.senderId]);

  const options: OptionType[] = senderIds.map((senderId) => ({
    isDisabled: senderId.status === SenderIdStatus.REJECTED_COMREG,
    label: `${senderId.id} (${getStatusTranslation(senderId.status, translate)})`,
    value: senderId.id,
  }));

  return (
    <FormItemLayout
      label={translate('Sender_ID')}
      description={translate('The_name_your_customers_will_see_when_receiving_SMS')}
    >
      <Field name="senderId" validate={validateSenderIdField}>
        {({ field, form }: FieldProps) => {
          const { errors, touched, isSubmitting } = form;
          const fieldError = errors[field.name] as string | undefined;
          const showError = !!fieldError;

          // Find the current senderId object and create option object
          const currentSenderId = (field.value as FormValues['senderId']) || undefined;
          const currentValue: OptionType | null =
            currentSenderId && currentSenderId.status !== SenderIdStatus.REJECTED_COMREG
              ? {
                  label: `${currentSenderId.id} (${getStatusTranslation(currentSenderId.status, translate)})`,
                  value: currentSenderId.id,
                  isDisabled: false,
                }
              : null;

          const handleSelectChange = (selected: OptionType | null) => {
            const selectedId = selected?.value as string | undefined;
            const selectedSender = senderIds.find((s) => s.id === selectedId);
            form.setFieldValue(field.name, selectedSender ?? undefined);
          };

          return (
            <StyledContainer>
              <StyledSelectWrapper>
                <Select
                  error={showError}
                  fieldError={showError ? fieldError : undefined}
                  isDisabled={isSubmitting || isSenderIdsLoading}
                  isLoading={isSenderIdsLoading}
                  onChange={handleSelectChange}
                  options={options}
                  placeholder={translate('No_Sender_ID_configured')}
                  value={currentValue}
                  variant="standard"
                />
              </StyledSelectWrapper>

              <StyledButtonWrapper>
                <LinkButton
                  color="primary"
                  fdKey="setup-sender-id-button"
                  to={`/${brandId}/communication/sms-registration`}
                  type="button"
                  variant="outlined"
                >
                  {translate('Create_Sender_ID')}
                </LinkButton>
              </StyledButtonWrapper>
            </StyledContainer>
          );
        }}
      </Field>
    </FormItemLayout>
  );
};

type MappedProps = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => ({
  translate: getTranslate(state.locale),
});

export default connect(mapStateToProps)(SenderIdField);
